import { BrandIdSchema } from '@estimating/branding';
import { EnvironmentSchema } from '@estimating/contracts';
import { isSentryDsn, SENTRY_DSN_FORMAT } from '@estimating/error-reporting';
import { z } from 'zod';

const SentryRuntimeConfigSchema = z.object({
  dsn: z.string().refine(isSentryDsn, SENTRY_DSN_FORMAT),
  /** Share of page loads and navigations traced, from 0 to 1. Tracing is off when absent. */
  tracesSampleRate: z.number().min(0).max(1).optional(),
});

/**
 * The brand's Firebase project, which people sign in through (D-036, D-057). These
 * values identify the project to the browser and aren't secrets. Other keys in the
 * Firebase console's snippet, such as `measurementId`, are dropped, so Analytics
 * never starts.
 */
const FirebaseRuntimeConfigSchema = z.object({
  apiKey: z.string().min(1),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  appId: z.string().min(1),
});
export type FirebaseRuntimeConfig = z.infer<typeof FirebaseRuntimeConfigSchema>;

/**
 * Served as `/config.json` by each stamp, so one build runs everywhere (D-002, D-021).
 * Locally it comes from `public/config.json`, plus a Sentry DSN from the environment
 * when one is set (vite/local-sentry-config.ts).
 */
export const RuntimeConfigSchema = z.object({
  brand: BrandIdSchema,
  environment: EnvironmentSchema,
  apiBaseUrl: z.url({ protocol: /^https?$/ }),
  /** Required: without it nobody can sign in, so the app shows the config error instead. */
  firebase: FirebaseRuntimeConfigSchema,
  /**
   * Single sign-on with the brand's shop management system (D-060), when the stamp
   * shares sign-ins with it: the domain the API sets the shared sign-in's cookies on,
   * such as imex.online, or null for the API's host alone, as locally. Off when absent.
   */
  sso: z.object({ cookieDomain: z.string().min(1).nullable() }).optional(),
  /** Error reporting to the stamp's Sentry project; off when absent. */
  sentry: SentryRuntimeConfigSchema.optional(),
});
export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

/** Developer tools, such as the router's devtools, show only where developers work (D-072). */
export function showsDeveloperTools({ environment }: Pick<RuntimeConfig, 'environment'>): boolean {
  return environment === 'local' || environment === 'dev';
}

export type RuntimeConfigResult =
  | { readonly ok: true; readonly config: RuntimeConfig }
  | { readonly ok: false; readonly reason: unknown };

/** Fetches and checks the stamp's `/config.json`. Never rejects: a failure is a result to show. */
export async function loadRuntimeConfig(
  fetchConfig: (url: string, init: RequestInit) => Promise<Response> = (url, init) =>
    fetch(url, init),
): Promise<RuntimeConfigResult> {
  try {
    const response = await fetchConfig('/config.json', { cache: 'no-store' });
    if (!response.ok) {
      return { ok: false, reason: new Error(`config.json returned HTTP ${response.status}`) };
    }
    const parsed = RuntimeConfigSchema.safeParse(await response.json());
    return parsed.success ? { ok: true, config: parsed.data } : { ok: false, reason: parsed.error };
  } catch (error) {
    return { ok: false, reason: error };
  }
}
